import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Slider,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Toolbar,
  IconButton,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Badge,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  GridOn as GridIcon,
  Straighten as RulerIcon,
  CropFree as MarginsIcon,
  SelectAll as SelectIcon,
  OpenWith as MoveIcon,
  AspectRatio as ResizeIcon,
  FormatAlignLeft as AlignLeftIcon,
  FormatAlignCenter as AlignCenterIcon,
  FormatAlignRight as AlignRightIcon,
  VerticalAlignTop as AlignTopIcon,
  VerticalAlignCenter as AlignMiddleIcon,
  VerticalAlignBottom as AlignBottomIcon,
  ContentCopy as DuplicateIcon,
  ExpandMore as ExpandMoreIcon,
  PhotoLibrary as BackgroundIcon,
  TextFields as TextIcon,
  Title as HeaderIcon,
  Person as PersonIcon,
  Subject as SubjectIcon,
  Article as BodyIcon,
  DescriptionOutlined as LayoutHeaderIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { apiFetch, createApiUrl } from '../../../utils/apiConfig';
import { getAuthHeaders } from '../../../utils/authUtils';
import { useSnackbar } from '../../CentralSnackbar';

// Seitenformat-Definitionen in mm (2x Skalierung für bessere Darstellung)
const PAGE_FORMATS = {
  a4: { width: 420, height: 594, label: 'A4' },
  letter: { width: 432, height: 558, label: 'US Letter' },
  legal: { width: 432, height: 712, label: 'US Legal' },
  a3: { width: 594, height: 840, label: 'A3' },
  custom: { width: 420, height: 594, label: 'Custom' }
};

// Standard-Seitenränder in mm (2x Skalierung)
const DEFAULT_MARGINS = {
  top: 50,
  bottom: 50,
  left: 50,
  right: 50
};

// Verfügbare Schriftarten (OHNE SCHNÖRKEL - Sans-Serif)
const AVAILABLE_FONTS = [
  { value: 'Arial', label: 'Arial' },
  { value: 'Helvetica', label: 'Helvetica' },
  { value: '"Segoe UI"', label: 'Segoe UI' },
  { value: 'Verdana', label: 'Verdana' },
  { value: 'Tahoma', label: 'Tahoma' },
  { value: '"Trebuchet MS"', label: 'Trebuchet MS' },
  { value: 'Calibri', label: 'Calibri' },
  { value: '"Lucida Grande"', label: 'Lucida Grande' }
];

// Bereichstypen (2x Skalierung) 
const AREA_TYPES = {
  letterhead: { icon: HeaderIcon, color: 'var(--rd-info)', defaultHeight: 120, defaultFontSize: 12, defaultFontFamily: '"Segoe UI"' },
  patientinfo: { icon: PersonIcon, color: 'var(--rd-chat-group)', defaultHeight: 60, defaultFontSize: 10, defaultFontFamily: '"Segoe UI"' },
  subject: { icon: SubjectIcon, color: 'var(--rd-success)', defaultHeight: 80, defaultFontSize: 12, defaultFontFamily: '"Segoe UI"' },
  greeting: { icon: TextIcon, color: 'var(--rd-text-secondary)', defaultHeight: 120, defaultFontSize: 11, defaultFontFamily: '"Segoe UI"' },
  body: { icon: BodyIcon, color: 'var(--rd-accent)', defaultHeight: 400, defaultFontSize: 11, defaultFontFamily: '"Segoe UI"' }
};

// Database-Felder für Subject-Dialog
const DATABASE_FIELDS = [
  'study_date',
  'study_description', 
  'patient_name',
  'patient_id',
  'study_id',
  'modality',
  'institution',
  'referring_physician_title',
  'referring_physician_first_name',
  'referring_physician_last_name'
];

// Standard-Bereiche erstellen
const createDefaultAreas = () => {
  // Hilfsfunktion für Zeilen zu Canvas-Einheiten
  const linesToCanvasUnits = (lines, fontSizePt) => {
    // DEBUG: 1 Zeile = fontSize * lineHeight (OHNE 2x-Faktor, da Canvas-Höhe direkt CSS entspricht)
    const lineHeightInCanvasUnits = fontSizePt * 0.706 * 1.2;
    // console.log(`DEBUG linesToCanvasUnits: ${lines} Zeilen, ${fontSizePt}pt -> ${lineHeightInCanvasUnits} pro Zeile -> total: ${lines * lineHeightInCanvasUnits}`);
    return lines * lineHeightInCanvasUnits;
  };

  return [
    // Bereiche für erste Seite
    {
      id: 'letterhead-default',
      type: 'letterhead',
      page: 'first',
      x: 50, // UI-Wert 25 * 2 = 50 Canvas-Einheiten
      y: 118, // UI-Wert 59 * 2 = 118 Canvas-Einheiten
      width: 150, // UI-Wert 75 * 2 = 150 Canvas-Einheiten
      height: linesToCanvasUnits(4, AREA_TYPES.letterhead.defaultFontSize), // 4 Zeilen
      fontSize: AREA_TYPES.letterhead.defaultFontSize,
      fontFamily: AREA_TYPES.letterhead.defaultFontFamily
    },
    {
      id: 'patientinfo-default',
      type: 'patientinfo',
      page: 'first',
      x: 50, // UI-Wert 25 * 2 = 50 Canvas-Einheiten
      y: 180, // UI-Wert 90 * 2 = 180 Canvas-Einheiten
      width: 260, // UI-Wert 130 * 2 = 260 Canvas-Einheiten
      height: linesToCanvasUnits(1, 11) + 2, // 1 Zeile + 2px Korrektur für Schriftgröße 11
      fontSize: 11,
      fontFamily: AREA_TYPES.patientinfo.defaultFontFamily
    },
    {
      id: 'subject-default',
      type: 'subject', 
      page: 'first',
      x: 50, // UI-Wert 25 * 2 = 50 Canvas-Einheiten
      y: 200, // UI-Wert 100 * 2 = 200 Canvas-Einheiten
      width: 260, // UI-Wert 130 * 2 = 260 Canvas-Einheiten
      height: linesToCanvasUnits(1, 11), // 1 Zeile
      fontSize: 11,
      fontFamily: AREA_TYPES.subject.defaultFontFamily
    },
    {
      id: 'greeting-default',
      type: 'greeting',
      page: 'first',
      x: 50, // UI-Wert 25 * 2 = 50 Canvas-Einheiten
      y: 220, // UI-Wert 110 * 2 = 220 Canvas-Einheiten
      width: 300, // UI-Wert 150 * 2 = 300 Canvas-Einheiten
      height: linesToCanvasUnits(5, 11), // 5 Zeilen für Grußformel
      fontSize: 11,
      fontFamily: AREA_TYPES.greeting.defaultFontFamily
    },
    {
      id: 'body-default',
      type: 'body',
      page: 'first', 
      x: 50, // UI-Wert 25 * 2 = 50 Canvas-Einheiten
      y: 280, // UI-Wert 140 * 2 = 280 Canvas-Einheiten (nach greeting verschoben)
      width: 300, // UI-Wert 150 * 2 = 300 Canvas-Einheiten
      height: linesToCanvasUnits(25, 11), // 25 Zeilen (reduziert wegen greeting)
      fontSize: 11,
      fontFamily: AREA_TYPES.body.defaultFontFamily
    },
    // Bereiche für Folgeseite
    {
      id: 'body-following-default',
      type: 'body',
      page: 'following', 
      x: 50, // UI-Wert 25 * 2 = 50 Canvas-Einheiten
      y: 60, // UI-Wert 30 * 2 = 60 Canvas-Einheiten 
      width: 320, // UI-Wert 160 * 2 = 320 Canvas-Einheiten
      height: linesToCanvasUnits(50, 11), // 50 Zeilen, Schriftgröße 11
      fontSize: 11,
      fontFamily: AREA_TYPES.body.defaultFontFamily
    }
  ];
};

const ReportLayout = () => {
  const { t } = useTranslation();
  const { showSuccess, showError, showWarning } = useSnackbar();
  const canvasRef = useRef(null);
  
  // Layout-Konfiguration
  const [layout, setLayout] = useState({
    pageFormat: 'a4',
    customSize: { width: 420, height: 594 },
    margins: { ...DEFAULT_MARGINS },
    backgroundImages: {
      firstPage: null,
      followingPages: null
    },
    areas: createDefaultAreas(),
    subjectTemplate: '',
    greetingTemplates: {
      male: 'Sehr geehrter Herr [referring_physician_last_name],\n\nvielen Dank für die freundliche Zuweisung des/der oben genannten Patient:in, bei der wir am [study_date] folgenden Befund erheben konnten:',
      female: 'Sehr geehrte Frau [referring_physician_last_name],\n\nvielen Dank für die freundliche Zuweisung des/der oben genannten Patient:in, bei der wir am [study_date] folgenden Befund erheben konnten:',
      diverse: 'Sehr geehrte/r [referring_physician_title] [referring_physician_last_name],\n\nvielen Dank für die freundliche Zuweisung des/der oben genannten Patient:in, bei der wir am [study_date] folgenden Befund erheben konnten:',
      none: 'Sehr geehrte Damen und Herren,\n\nvielen Dank für die freundliche Zuweisung des/der oben genannten Patient:in, bei der wir am [study_date] folgenden Befund erheben konnten:'
    },
    greetingEnd: 'Mit freundlichen Grüßen'
  });
  
  // UI-States
  const [selectedAreaId, setSelectedAreaId] = useState('letterhead-default');
  const [zoom, setZoom] = useState(1.311);
  const [showRulers, setShowRulers] = useState(true);
  const [showMargins, setShowMargins] = useState(true);
  const [showGrid, setShowGrid] = useState(false);
  const [currentPage, setCurrentPage] = useState('first');
  const [isEditingLayout, setIsEditingLayout] = useState(false); // Neue State: Layout-Edit-Modus
  
  // Helper function to handle page changes and auto-select appropriate area
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    
    // Auto-select appropriate area for the new page
    if (newPage === 'following') {
      // If switching to Following Page, select the body area for following page
      const followingBodyArea = layout.areas.find(area => 
        area.type === 'body' && area.page === 'following'
      );
      if (followingBodyArea) {
        setSelectedAreaId(followingBodyArea.id);
      }
    } else if (newPage === 'first') {
      // If switching to First Page, select letterhead by default
      const lettterheadArea = layout.areas.find(area => 
        area.type === 'letterhead' && area.page === 'first'
      );
      if (lettterheadArea) {
        setSelectedAreaId(lettterheadArea.id);
      }
    }
  };
  
  // Mandanten-System
  const [currentTenant, setCurrentTenant] = useState(1); // Default to first tenant
  const [availableTenants, setAvailableTenants] = useState([]);
  const [currentLayout, setCurrentLayout] = useState(null); // Ein Layout pro Tenant
  const [hasExistingLayout, setHasExistingLayout] = useState(false);
  
  // Dialog-States
  const [subjectDialogOpen, setSubjectDialogOpen] = useState(false);
  const [greetingDialogOpen, setGreetingDialogOpen] = useState(false);
  const [greetingTabValue, setGreetingTabValue] = useState(0);
  
  // Feedback-States
  const [isLoading, setIsLoading] = useState(false);
  

  // Berechnete Werte
  const currentFormat = layout.pageFormat === 'custom' ? layout.customSize : PAGE_FORMATS[layout.pageFormat];
  const pageWidth = currentFormat.width * zoom;
  const pageHeight = currentFormat.height * zoom;
  
  // Canvas-Größe für die Darstellung - minimaler unterer Abstand
  const canvasWidth = pageWidth + 50; // 25px links (Lineal) + 25px rechts
  const canvasHeight = pageHeight + 60; // 25px oben (Lineal) + 35px für Labels + 5px unten

  useEffect(() => {
    loadTenants();
  }, []);

  useEffect(() => {
    if (currentTenant) {
      loadLayoutForCurrentTenant();
    }
  }, [currentTenant]);

  const loadTenants = async () => {
    try {
      const response = await apiFetch('/tenants/', {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const tenants = await response.json();
        setAvailableTenants(tenants);
        
        // Set first tenant as default if none selected
        if (tenants.length > 0 && !currentTenant) {
          setCurrentTenant(tenants[0].id);
        }
      }
    } catch (err) {
      console.error('Failed to load tenants:', err);
      showError(t('admin.reportingManagement.reportLayout.errors.loadTenantsError'), 'ERR-LAYOUT-001');
    }
  };

  // Hilfsfunktion um den Namen des aktuellen Tenants zu bekommen
  const getCurrentTenantName = () => {
    const tenant = availableTenants.find(t => t.id === currentTenant);
    return tenant ? tenant.bezeichnung : 'Unbekannter Tenant';
  };

  const loadLayoutForCurrentTenant = async () => {
    try {
      const response = await apiFetch(`/report-layout/?tenant_id=${currentTenant}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const layouts = await response.json();
        
        if (layouts.length > 0) {
          // Tenant hat bereits ein Layout
          const existingLayout = layouts[0];
          setCurrentLayout(existingLayout);
          setHasExistingLayout(true);
          
          // Layout-Daten in Frontend-Format konvertieren und laden
          const frontendLayout = {
            pageFormat: existingLayout.page_format,
            customSize: existingLayout.custom_size || { width: 420, height: 594 },
            margins: existingLayout.margins,
            backgroundImages: existingLayout.background_images || { firstPage: null, followingPages: null },
            areas: existingLayout.areas && existingLayout.areas.length > 0 ? 
              existingLayout.areas.map(area => {
                const fontSize = area.fontSize || AREA_TYPES[area.type]?.defaultFontSize || 12;
                return {
                  ...area,
                  // Stelle sicher, dass fontSize gesetzt ist (falls nicht in DB vorhanden)
                  fontSize: fontSize
                };
              }) : createDefaultAreas(),
            subjectTemplate: existingLayout.subject_template || '',
            greetingTemplates: existingLayout.greeting_templates || {
              male: 'Sehr geehrter Herr [referring_physician_last_name],\n\nvielen Dank für die freundliche Zuweisung des/der oben genannten Patient:in, bei der wir am [study_date] folgenden Befund erheben konnten:',
              female: 'Sehr geehrte Frau [referring_physician_last_name],\n\nvielen Dank für die freundliche Zuweisung des/der oben genannten Patient:in, bei der wir am [study_date] folgenden Befund erheben konnten:',
              diverse: 'Sehr geehrte/r [referring_physician_title] [referring_physician_last_name],\n\nvielen Dank für die freundliche Zuweisung des/der oben genannten Patient:in, bei der wir am [study_date] folgenden Befund erheben konnten:',
              none: 'Sehr geehrte Damen und Herren,\n\nvielen Dank für die freundliche Zuweisung des/der oben genannten Patient:in, bei der wir am [study_date] folgenden Befund erheben konnten:'
            },
            greetingEnd: existingLayout.greeting_end || 'Mit freundlichen Grüßen'
          };
          setLayout(frontendLayout);
        } else {
          // Tenant hat noch kein Layout - Default-Layout hart codiert laden
          setCurrentLayout(null);
          setHasExistingLayout(false);
          
          // Standard-Layout mit Default-Areas laden (hart codiert)
          const defaultLayout = {
            pageFormat: 'a4',
            customSize: { width: 420, height: 594 },
            margins: { ...DEFAULT_MARGINS },
            backgroundImages: { firstPage: null, followingPages: null },
            areas: createDefaultAreas(), // Hart codierte Default-Areas
            subjectTemplate: '',
            greetingTemplates: {
              male: 'Sehr geehrter Herr [referring_physician_last_name],\n\nvielen Dank für die freundliche Zuweisung des/der oben genannten Patient:in, bei der wir am [study_date] folgenden Befund erheben konnten:',
              female: 'Sehr geehrte Frau [referring_physician_last_name],\n\nvielen Dank für die freundliche Zuweisung des/der oben genannten Patient:in, bei der wir am [study_date] folgenden Befund erheben konnten:',
              diverse: 'Sehr geehrte/r [referring_physician_title] [referring_physician_last_name],\n\nvielen Dank für die freundliche Zuweisung des/der oben genannten Patient:in, bei der wir am [study_date] folgenden Befund erheben konnten:',
              none: 'Sehr geehrte Damen und Herren,\n\nvielen Dank für die freundliche Zuweisung des/der oben genannten Patient:in, bei der wir am [study_date] folgenden Befund erheben konnten:'
            },
            greetingEnd: 'Mit freundlichen Grüßen'
          };
          setLayout(defaultLayout);
        }
      }
    } catch (err) {
      console.log('Failed to load layout for tenant:', err);
      setHasExistingLayout(false);
      setCurrentLayout(null);
      
      // Bei Fehler auch Default-Layout hart codiert laden
      const defaultLayout = {
        pageFormat: 'a4',
        customSize: { width: 420, height: 594 },
        margins: { ...DEFAULT_MARGINS },
        backgroundImages: { firstPage: null, followingPages: null },
        areas: createDefaultAreas(), // Hart codierte Default-Areas
        subjectTemplate: '',
        greetingEnd: 'Mit freundlichen Grüßen'
      };
      setLayout(defaultLayout);
    }
  };


  // Hilfsfunktion für automatisches Speichern nach Upload
  const saveLayoutToDB = async (layoutToSave) => {
    try {
      // Convert frontend format to backend format
      const backendLayout = {
        name: getCurrentTenantName(),
        description: `Layout für ${getCurrentTenantName()}`,
        page_format: layoutToSave.pageFormat,
        custom_size: layoutToSave.pageFormat === 'custom' ? layoutToSave.customSize : null,
        margins: layoutToSave.margins,
        background_images: layoutToSave.backgroundImages,
        areas: layoutToSave.areas,
        subject_template: layoutToSave.subjectTemplate,
        greeting_templates: layoutToSave.greetingTemplates,
        greeting_end: layoutToSave.greetingEnd,
        tenant_id: currentTenant,
        is_default: true,
        is_active: true
      };
      
      
      let response;
      
      if (hasExistingLayout && currentLayout) {
        // Layout existiert bereits - IMMER UPDATE (überschreiben)
        response = await apiFetch(`/report-layout/${currentLayout.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify(backendLayout)
        });
      } else {
        // Kein Layout für Tenant - CREATE (nur beim ersten Mal)
        response = await apiFetch('/report-layout/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify(backendLayout)
        });
        
        // Nach erstem Speichern: hasExistingLayout auf true setzen
        if (response.ok) {
          const newLayout = await response.json();
          setHasExistingLayout(true);
          setCurrentLayout(newLayout);
        }
      }
      
      if (response.ok) {
        // Layout saved successfully
      } else {
        const errorData = await response.json();
        console.error('Save failed:', errorData);
      }
    } catch (err) {
      console.log('Auto-save failed:', err);
    }
  };

  const saveLayout = async () => {
    try {
      setIsLoading(true);
      
      // Convert frontend format to backend format
      const backendLayout = {
        name: getCurrentTenantName(),
        description: `Layout für ${getCurrentTenantName()}`,
        page_format: layout.pageFormat,
        custom_size: layout.pageFormat === 'custom' ? layout.customSize : null,
        margins: layout.margins,
        background_images: layout.backgroundImages,
        areas: layout.areas,
        subject_template: layout.subjectTemplate,
        greeting_templates: layout.greetingTemplates,
        greeting_end: layout.greetingEnd,
        tenant_id: currentTenant,
        is_default: true,
        is_active: true
      };
      
      let response;
      
      if (hasExistingLayout && currentLayout) {
        // UPDATE existierendes Layout
        response = await apiFetch(`/report-layout/${currentLayout.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify(backendLayout)
        });
      } else {
        // CREATE neues Layout
        response = await apiFetch('/report-layout/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders()
          },
          body: JSON.stringify(backendLayout)
        });
      }

      if (response.ok) {
        const responseData = await response.json();
        
        // Falls neues Layout erstellt wurde, States aktualisieren
        if (!hasExistingLayout) {
          setHasExistingLayout(true);
          setCurrentLayout(responseData);
        }
        
        showSuccess(t('admin.reportingManagement.reportLayout.actions.saveSuccess'));
        setIsEditingLayout(false); // Edit-Modus nach erfolgreichem Speichern deaktivieren
      } else {
        const errorData = await response.json();
        console.error('Backend error saving layout:', errorData);
        showError(errorData.detail || t('admin.reportingManagement.reportLayout.actions.saveError'), 'ERR-LAYOUT-002');
      }
    } catch (err) {
      console.error('Network error saving layout:', err);
      showError(t('admin.reportingManagement.reportLayout.actions.saveError'), 'ERR-LAYOUT-003');
    } finally {
      setIsLoading(false);
    }
  };


  const handlePageFormatChange = (format) => {
    setLayout(prev => ({
      ...prev,
      pageFormat: format,
      areas: format !== prev.pageFormat ? [] : prev.areas // Reset areas wenn Format ändert
    }));
    setSelectedAreaId(null);
  };

  const handleAddArea = (type) => {
    const newArea = {
      id: Date.now(),
      type,
      x: 50,
      y: 50,
      width: currentFormat.width - 100,
      height: AREA_TYPES[type].defaultHeight,
      page: currentPage === 'first' ? 'first' : 'following',
      fontSize: AREA_TYPES[type].defaultFontSize
    };
    
    setLayout(prev => ({
      ...prev,
      areas: [...prev.areas, newArea]
    }));
    setSelectedAreaId(newArea.id);
  };

  const handleAreaUpdate = (areaId, updates) => {
    const updatedLayout = {
      ...layout,
      areas: layout.areas.map(area => 
        area.id === areaId ? { ...area, ...updates } : area
      )
    };
    
    setLayout(updatedLayout);
    // KEIN automatisches Speichern mehr! Nur bei "Layout speichern" Button
  };

  const handleAreaDelete = (areaId) => {
    setLayout(prev => ({
      ...prev,
      areas: prev.areas.filter(area => area.id !== areaId)
    }));
    if (selectedAreaId === areaId) {
      setSelectedAreaId(null);
    }
  };

  const handleAreaDuplicate = (areaId) => {
    const area = layout.areas.find(a => a.id === areaId);
    if (area) {
      const newArea = {
        ...area,
        id: Date.now(),
        x: area.x + 20,
        y: area.y + 20
      };
      setLayout(prev => ({
        ...prev,
        areas: [...prev.areas, newArea]
      }));
    }
  };

  // Hilfsfunktion: Konvertiere Canvas-Einheiten zu Zeilen basierend auf Schriftgröße
  const canvasUnitsToLines = (heightInCanvasUnits, fontSizePt) => {
    // DEBUG: 1 Zeile = fontSize * lineHeight (OHNE 2x-Faktor, da Canvas-Höhe direkt CSS entspricht)
    const lineHeightInCanvasUnits = fontSizePt * 0.706 * 1.2;
    const lines = Math.round(heightInCanvasUnits / lineHeightInCanvasUnits);
    // console.log(`DEBUG canvasUnitsToLines: ${heightInCanvasUnits} Canvas-Einheiten, ${fontSizePt}pt -> ${lineHeightInCanvasUnits} pro Zeile -> ${lines} Zeilen`);
    return lines;
  };

  // Hilfsfunktion: Konvertiere Zeilen zu Canvas-Einheiten basierend auf Schriftgröße
  const linesToCanvasUnits = (lines, fontSizePt) => {
    // DEBUG: 1 Zeile = fontSize * lineHeight (OHNE 2x-Faktor, da Canvas-Höhe direkt CSS entspricht)
    const lineHeightInCanvasUnits = fontSizePt * 0.706 * 1.2;
    // console.log(`DEBUG linesToCanvasUnits (runtime): ${lines} Zeilen, ${fontSizePt}pt -> ${lineHeightInCanvasUnits} pro Zeile -> total: ${lines * lineHeightInCanvasUnits}`);
    return lines * lineHeightInCanvasUnits;
  };

  const handleFileUpload = async (file, type) => {
    // Akzeptiere PDF oder PNG
    if (!file || (file.type !== 'application/pdf' && file.type !== 'image/png')) {
      showWarning(t('admin.reportingManagement.reportLayout.backgroundImages.uploadErrorFormat'));
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('page_type', type);

      const response = await apiFetch('/report-layout/upload-background', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        
        // Update layout state
        const updatedLayout = {
          ...layout,
          backgroundImages: {
            ...layout.backgroundImages,
            [type]: {
              filename: data.filename,
              url: createApiUrl(`report-layout/background/${data.filename}`),
              size: data.file_size
            }
          }
        };
        
        setLayout(updatedLayout);
        
        // Automatisch in Datenbank speichern
        await saveLayoutToDB(updatedLayout);
        
        showSuccess(t('admin.reportingManagement.reportLayout.backgroundImages.uploadSuccess'));
      } else {
        const errorData = await response.json();
        console.error('Backend error uploading background:', errorData);
        showError(errorData.detail || t('admin.reportingManagement.reportLayout.backgroundImages.uploadError'), 'ERR-LAYOUT-004');
      }
    } catch (err) {
      console.error('Network error uploading background:', err);
      showError(t('admin.reportingManagement.reportLayout.backgroundImages.uploadError'), 'ERR-LAYOUT-005');
    }
  };

  const handleRemoveBackground = (type) => {
    const updatedLayout = {
      ...layout,
      backgroundImages: {
        ...layout.backgroundImages,
        [type]: null
      }
    };
    
    setLayout(updatedLayout);
    // KEIN automatisches Speichern mehr! Nur bei "Layout speichern" Button
  };

  const insertDatabaseField = (field) => {
    const fieldTag = `[${field}]`;
    setLayout(prev => ({
      ...prev,
      subjectTemplate: prev.subjectTemplate + fieldTag
    }));
  };

  const handleSaveLayout = () => {
    saveLayout();
  };

  const selectedArea = layout.areas.find(area => area.id === selectedAreaId);
  const currentAreas = layout.areas.filter(area => 
    currentPage === 'first' 
      ? area.page === 'first' 
      : area.page === 'following'
  );

  // Render-Methoden für Canvas-Elemente - Word-ähnliche minimale Lineale
  const renderRulers = () => {
    if (!showRulers) return null;
    
    // Berechne cm-Schritte (angepasst für 2x Skalierung)
    const cmWidth = 20 * zoom; // 1cm = 20 Einheiten (da PAGE_FORMATS verdoppelt)
    const cmHeight = 20 * zoom;
    

  return (
      <>
        {/* Horizontales Lineal - Word-Stil */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 25,
            width: pageWidth, // Genau die Seitenbreite
            height: 25,
            backgroundColor: 'var(--rd-ruler-bg)',
            borderBottom: '1px solid var(--rd-divider)',
            overflow: 'hidden' // Verhindert Überlauf
          }}
        >
          {/* cm-Markierungen horizontal - nur bis Seitenrand */}
          {Array.from({ length: Math.floor(currentFormat.width / 20) + 1 }, (_, i) => (
            <Box key={i}>
              {/* Hauptmarkierung alle 1cm - nur wenn innerhalb der Seite */}
              {(i * cmWidth) <= pageWidth && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: (i * cmWidth),
                    bottom: 0,
                    height: 8,
                    width: 1,
                    backgroundColor: 'var(--rd-ruler-dark)'
                  }}
                />
              )}
              {/* cm-Beschriftung bei jeder cm-Markierung */}
              {i > 0 && (i * cmWidth) <= pageWidth && (
                <Typography
                  sx={{
                    position: 'absolute',
                    left: (i * cmWidth) - 8,
                    top: 2,
                    fontSize: '9px',
                    color: 'var(--rd-text-secondary)',
                    fontFamily: 'monospace',
                    lineHeight: 1
                  }}
                >
                  {i}
                </Typography>
              )}
              
              {/* 5mm Zwischenmarkierungen */}
              {i < Math.floor(currentFormat.width / 20) && ((i * cmWidth) + (cmWidth / 2)) <= pageWidth && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: (i * cmWidth) + (cmWidth / 2),
                    bottom: 0,
                    height: 4,
                    width: 1,
                    backgroundColor: 'var(--rd-ruler-light)'
                  }}
                />
              )}
            </Box>
          ))}
        </Box>
        
        {/* Vertikales Lineal - Word-Stil */}
        <Box
          sx={{
            position: 'absolute',
            top: 25,
            left: 0,
            width: 25,
            height: pageHeight, // Genau die Seitenhöhe
            backgroundColor: 'var(--rd-ruler-bg)',
            borderRight: '1px solid var(--rd-divider)',
            overflow: 'hidden' // Verhindert Überlauf
          }}
        >
          {/* cm-Markierungen vertikal - nur bis Seitenrand */}
          {Array.from({ length: Math.floor(currentFormat.height / 20) + 1 }, (_, i) => (
            <Box key={i}>
              {/* Hauptmarkierung alle 1cm - nur wenn innerhalb der Seite */}
              {(i * cmHeight) <= pageHeight && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: (i * cmHeight),
                    right: 0,
                    width: 8,
                    height: 1,
                    backgroundColor: 'var(--rd-ruler-dark)'
                  }}
                />
              )}
              {/* cm-Beschriftung bei jeder cm-Markierung */}
              {i > 0 && (i * cmHeight) <= pageHeight && (
                <Typography
                  sx={{
                    position: 'absolute',
                    right: 10,
                    top: (i * cmHeight) - 12,
                    fontSize: '9px',
                    color: 'var(--rd-text-secondary)',
                    fontFamily: 'monospace',
                    lineHeight: 1,
                    transform: 'rotate(-90deg)',
                    transformOrigin: 'center'
                  }}
                >
                  {i}
                </Typography>
              )}
              
              {/* 5mm Zwischenmarkierungen */}
              {i < Math.floor(currentFormat.height / 20) && ((i * cmHeight) + (cmHeight / 2)) <= pageHeight && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: (i * cmHeight) + (cmHeight / 2),
                    right: 0,
                    width: 4,
                    height: 1,
                    backgroundColor: 'var(--rd-ruler-light)'
                  }}
                />
              )}
            </Box>
          ))}
        </Box>
      </>
    );
  };

  const renderPage = () => {
    return (
      <Box
        ref={canvasRef}
        sx={{
          position: 'relative',
          width: pageWidth,
          height: pageHeight,
          backgroundColor: 'white',
          border: 2,
          borderColor: 'divider',
          boxShadow: 2,
          overflow: 'visible', // Labels sollen sichtbar sein
          cursor: 'default'
        }}
      >
        {/* Hintergrundbild - PNG oder PDF */}
        {((currentPage === 'first' && layout.backgroundImages.firstPage) ||
          (currentPage === 'following' && layout.backgroundImages.followingPages)) ? (() => {
            const bgImage = currentPage === 'first' 
              ? layout.backgroundImages.firstPage 
              : layout.backgroundImages.followingPages;
            const isPNG = bgImage?.filename?.endsWith('.png') || bgImage?.url?.endsWith('.png');
            
            return (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 0.3,
                  pointerEvents: 'none',
                  overflow: 'hidden'
                }}
              >
                {isPNG ? (
                  // PNG als img-Tag
                  <img
                    src={bgImage.url}
                    alt="Background"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                  />
                ) : (
                  // PDF als iframe
                  <iframe
                    src={`${bgImage.url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      transform: 'scale(1)',
                      transformOrigin: 'top left',
                      overflow: 'hidden'
                    }}
                    title="PDF Background"
                    scrolling="no"
                  />
                )}
              </Box>
            );
          })() : null}

        {/* Seitenränder */}
        {showMargins && (
          <>
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: layout.margins.top * zoom, // Bereits korrekte 2x-Einheiten
              backgroundColor: 'var(--rd-margin-bg)',
              borderBottom: '1px dashed var(--rd-margin-border)',
              pointerEvents: 'none'
            }} />
            <Box sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: layout.margins.bottom * zoom, // Bereits korrekte 2x-Einheiten
              backgroundColor: 'var(--rd-margin-bg)',
              borderTop: '1px dashed var(--rd-margin-border)',
              pointerEvents: 'none'
            }} />
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              width: layout.margins.left * zoom, // Bereits korrekte 2x-Einheiten
              backgroundColor: 'var(--rd-margin-bg)',
              borderRight: '1px dashed var(--rd-margin-border)',
              pointerEvents: 'none'
            }} />
            <Box sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              width: layout.margins.right * zoom, // Bereits korrekte 2x-Einheiten
              backgroundColor: 'var(--rd-margin-bg)',
              borderLeft: '1px dashed var(--rd-margin-border)',
              pointerEvents: 'none'
            }} />
          </>
        )}

        {/* Raster */}
        {showGrid && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 4px, rgba(0,0,0,0.1) 5px), repeating-linear-gradient(90deg, transparent, transparent 4px, rgba(0,0,0,0.1) 5px)',
              backgroundSize: `${5 * zoom}px ${5 * zoom}px`,
              pointerEvents: 'none'
            }}
          />
        )}

        {/* Layout-Bereiche */}
        {currentAreas.map(renderArea)}
      </Box>
    );
  };

  const renderArea = (area) => {
    const areaType = AREA_TYPES[area.type];
    const isSelected = selectedAreaId === area.id;
    
    // Korrekte Schriftgröße-Berechnung für A4-Skalierung
    // 1pt = 1/72 Inch = ~0.353mm, in unserem 2x-System = 0.706 Einheiten
    const fontSizePt = area.fontSize || areaType.defaultFontSize;
    const fontSize = fontSizePt * 0.706 * zoom; // Korrekte Skalierung
    const lineHeight = fontSize * 1.2; // Standard Zeilenhöhe
    
    // DEBUG: Log für CSS-Darstellung
    // if (area.type === 'letterhead') {
    //   console.log(`DEBUG renderArea ${area.type}: Canvas-Höhe=${area.height}, CSS fontSize=${fontSize}, CSS lineHeight=${lineHeight}, Zoom=${zoom}`);
    // }
    
    // Beispieltexte für verschiedene Bereiche
    const getExampleText = (type) => {
      switch (type) {
        case 'letterhead':
          return `Visus Private Practice
Gesundheitscampus-Sued 15
44801 Bochum
Deutschland`;
        case 'patientinfo':
          return `${t('patient.other')}: Mustermann, Max, *11.06.1962, Patienten-ID 123456`;
        case 'subject':
          return layout.subjectTemplate || 'Untersuchung vom [Study_date]';
        case 'greeting':
          return layout.greetingTemplates?.male || 'Sehr geehrter Herr Dr. Mustermann,\n\nvielen Dank für die freundliche Zuweisung des/der oben genannten Patient:in, bei der wir am 25.01.2019 folgenden Befund erheben konnten:';
        case 'body':
          return `Dear Sir/Madame,

Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet.

Duis autem vel eum iriure dolor in hendrerit in vulputate velit esse molestie consequat, vel illum dolore eu feugiat nulla facilisis at vero eros et accumsan et iusto odio dignissim qui blandit praesent luptatum zzril delenit augue duis dolore te feugait nulla facilisi.`;
        default:
          return '';
      }
    };
    
    return (
      <Box
        key={area.id}
        sx={{
          position: 'absolute',
          left: area.x * zoom,
          top: area.y * zoom,
          width: area.width * zoom,
          height: (area.height + (area.type === 'patientinfo' && area.fontSize === 11 ? 1 : 0)) * zoom, // +1px Korrektur für Patienteninformation bei Schriftgröße 11
          border: 2,
          borderColor: isSelected ? areaType.color : 'transparent',
          backgroundColor: isSelected ? `${areaType.color}20` : 'transparent',
          cursor: 'pointer',
          overflow: 'hidden',
          '&:hover': {
            borderColor: areaType.color,
            backgroundColor: `${areaType.color}20`
          }
        }}
        onClick={() => setSelectedAreaId(area.id)}
      >
        {/* Area Label */}
        <Box
          sx={{
            position: 'absolute',
            top: -30, // Mehr Platz nach oben
            left: 0,
            backgroundColor: areaType.color,
            color: 'white',
            px: 1,
            py: 0.5,
            fontSize: '12px',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            zIndex: 10,
            whiteSpace: 'nowrap' // Verhindert Umbruch
          }}
        >
          {React.createElement(areaType.icon, { sx: { fontSize: 14 } })}
          {t(`admin.reportingManagement.reportLayout.areas.${area.type}`)}
        </Box>

        {/* Beispieltext */}
        <Box
          sx={{
            p: 0, // Kein Padding - Text soll direkt am Rand beginnen
            height: '100%',
            overflow: 'hidden',
            fontSize: fontSize,
            lineHeight: `${lineHeight}px`,
            fontFamily: area.fontFamily || '"Segoe UI"',
            color: 'rgba(0,0,0,0.7)',
            whiteSpace: 'pre-line',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: area.type === 'subject' ? 'center' : 'flex-start', // Subject zentriert, andere oben
            alignItems: 'flex-start' // Alle linksbündig und oben ausgerichtet
          }}
        >
          {getExampleText(area.type)}
        </Box>

      </Box>
    );
  };


  return (
    <Box sx={{ p: 3, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LayoutHeaderIcon color="primary" />
          {t('admin.reportingManagement.reportLayout.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('admin.reportingManagement.reportLayout.description')}
        </Typography>
      </Box>

      {/* Mandanten-Auswahl Header */}
      <Card sx={{ mb: 2, p: 2, minHeight: 80 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 64, gap: 2 }}>
          {/* Tenant-Auswahl */}
          <Box sx={{ width: 300 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('admin.reportingManagement.reportLayout.tenant')}</InputLabel>
              <Select
                value={currentTenant || ''}
                onChange={(e) => {
                  setCurrentTenant(parseInt(e.target.value));
                  setIsEditingLayout(false); // Edit-Modus zurücksetzen bei Tenant-Wechsel
                }}
                label={t('admin.reportingManagement.reportLayout.tenant')}
              >
                {availableTenants.map(tenant => (
                  <MenuItem key={tenant.id} value={tenant.id}>
                    {tenant.bezeichnung} ({tenant.hl7_name})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          
          {/* Edit/Create Layout Button */}
          <Button
            variant="outlined"
            color="primary"
            disabled={!currentTenant}
            onClick={() => {
              // Edit-Modus aktivieren - egal ob neues oder existierendes Layout
              setIsEditingLayout(true);
              
              if (!hasExistingLayout) {
                // Neues Layout anlegen - Default-Areas hart codiert laden
                console.log('Creating new layout for tenant:', currentTenant);
                const newLayout = {
                  pageFormat: 'a4',
                  customSize: { width: 420, height: 594 },
                  margins: { ...DEFAULT_MARGINS },
                  backgroundImages: { firstPage: null, followingPages: null },
                  areas: createDefaultAreas(), // Hart codierte Default-Areas
                  subjectTemplate: '',
                  greetingEnd: 'Mit freundlichen Grüßen'
                };
                setLayout(newLayout);
                setHasExistingLayout(false); // Bleibt false bis erstes Speichern
              } else {
              }
            }}
            className="button"
          >
            {hasExistingLayout ? t('admin.reportingManagement.reportLayout.editLayout') : t('admin.reportingManagement.reportLayout.createLayout')}
          </Button>
          
          {/* Flexibler Spacer - nimmt allen verfügbaren Platz */}
          <Box sx={{ flexGrow: 1 }} />
          
          {/* Save Button - GANZ RECHTS */}
          <Button
            variant="contained"
            color="primary"
            disabled={!currentTenant}
            onClick={handleSaveLayout}
            className="button"
            startIcon={<SaveIcon />}
          >
            {t('admin.reportingManagement.reportLayout.actions.save')}
          </Button>
        </Box>
      </Card>

       {/* Main Layout - 3 Spalten: 20%, 20%, 60% */}
       <Box sx={{ 
         display: 'flex', 
         gap: 2,
         opacity: isEditingLayout ? 1 : 0.3, // Ausgegraut wenn nicht im Edit-Modus
         pointerEvents: isEditingLayout ? 'auto' : 'none', // Deaktiviert wenn nicht im Edit-Modus
         position: 'relative', 
         flex: 1, 
         minHeight: 0,
         flexDirection: { xs: 'column', lg: 'row' } // Responsive: Spalten auf mobil, Zeilen auf Desktop
       }}>
         {/* Overlay wenn Layout-Editor deaktiviert - direkt unter Tenant Header */}
         {!isEditingLayout && (
           <Box sx={{
             position: 'absolute',
             top: 0,
             left: 0,
             right: 0,
             bottom: 0,
             display: 'flex',
             alignItems: 'center',
             justifyContent: 'center',
             zIndex: 1000,
             backgroundColor: 'rgba(255, 255, 255, 0.8)',
             backdropFilter: 'blur(2px)'
           }}>
             <Paper sx={{
               p: 3,
               textAlign: 'center',
               backgroundColor: 'rgba(255, 255, 255, 0.95)',
               boxShadow: 3
             }}>
               <Typography variant="h6" gutterBottom>
                 {t('admin.reportingManagement.reportLayout.overlay.title')}
               </Typography>
               <Typography variant="body2" color="text.secondary">
                 {t('admin.reportingManagement.reportLayout.overlay.message')}
        </Typography>
      </Paper>
           </Box>
         )}
        {/* Neue Struktur: Linke + Mittlere Spalte Container (40%) */}
        <Box sx={{ 
          width: { xs: '100%', lg: '40%' }, 
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          flex: { xs: 'none', lg: '0 0 40%' }
        }}>
          {/* Obere Zeile: Seitenformat (links) + Hintergrundbilder (rechts) */}
          <Box sx={{ 
            display: 'flex', 
            gap: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'stretch' // Beide Cards bekommen die gleiche Höhe
          }}>
            {/* Seitenformat (50% der oberen Zeile) */}
            <Box sx={{ flex: 1 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('admin.reportingManagement.reportLayout.pageFormat.title')}
        </Typography>
                  <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                    <Select
                      value={layout.pageFormat}
                      onChange={(e) => handlePageFormatChange(e.target.value)}
                    >
                      {Object.entries(PAGE_FORMATS).map(([key, format]) => (
                        <MenuItem key={key} value={key}>
                          {t(`admin.reportingManagement.reportLayout.pageFormat.${key}`)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {layout.pageFormat === 'custom' && (
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                      <TextField
                        size="small"
                        label="W"
                        type="number"
                        value={layout.customSize.width}
                        onChange={(e) => setLayout(prev => ({
                          ...prev,
                          customSize: { ...prev.customSize, width: Number(e.target.value) }
                        }))}
                        sx={{ width: 60 }}
                      />
                      <TextField
                        size="small"
                        label="H"
                        type="number"
                        value={layout.customSize.height}
                        onChange={(e) => setLayout(prev => ({
                          ...prev,
                          customSize: { ...prev.customSize, height: Number(e.target.value) }
                        }))}
                        sx={{ width: 60 }}
                      />
                    </Box>
                  )}

                  <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>
                    {t('admin.reportingManagement.reportLayout.pageFormat.margins')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {Object.entries(layout.margins).map(([key, value]) => (
                      <TextField
                        key={key}
                        size="small"
                        label={key.charAt(0).toUpperCase()}
                        type="number"
                        value={value / 2}
                        onChange={(e) => setLayout(prev => ({
                          ...prev,
                          margins: { ...prev.margins, [key]: Number(e.target.value) * 2 }
                        }))}
                        sx={{ width: 70 }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Hintergrundbilder (50% der oberen Zeile) */}
            <Box sx={{ flex: 1 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('admin.reportingManagement.reportLayout.backgroundImages.title')}
                  </Typography>
                  {Object.entries(layout.backgroundImages).map(([type, image]) => (
                    <Box key={type} sx={{ mb: 1.5 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', mb: 0.5 }}>
                        {t(`admin.reportingManagement.reportLayout.backgroundImages.${type}`)}
                      </Typography>
                      {image ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip
                            label={(() => {
                              // Extrahiere den reinen Dateinamen ohne Präfix
                              const filename = layout.backgroundImages[type].filename;
                              // Entferne Präfixe wie "firstPage_123456_" oder "followingPages_123456_"
                              const cleanFilename = filename.replace(/^(firstPage|followingPages)_\d+_/, '');
                              return cleanFilename;
                            })()}
                            size="small"
                            variant="outlined"
                            sx={{ 
                              fontSize: '0.7rem',
                              maxWidth: '200px', // Begrenzte maximale Breite
                              '& .MuiChip-label': {
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '100%'
                              }
                            }}
                          />
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveBackground(type)}
                          >
                            <DeleteIcon sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Box>
                      ) : (
                        <>
                          <input
                            type="file"
                            accept=".pdf,.png"
                            style={{ display: 'none' }}
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) handleFileUpload(file, type);
                            }}
                            id={`upload-${type}`}
                          />
                          <label htmlFor={`upload-${type}`}>
                            <Button
                              variant="outlined"
                              component="span"
                              size="small"
                              startIcon={<UploadIcon />}
                              sx={{ fontSize: '0.7rem', width: '100%' }}
                            >
                              Upload
                            </Button>
                          </label>
                        </>
                      )}
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Box>
          </Box>

          {/* Untere Zeile: Layout-Bereiche (links) + Area Configuration (rechts) */}
          <Box sx={{ 
            display: 'flex', 
            gap: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'stretch' // Beide Cards bekommen die gleiche Höhe
          }}>
            {/* Layout-Bereiche (50% der unteren Zeile) */}
            <Box sx={{ flex: 1 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('admin.reportingManagement.reportLayout.layoutAreas.title')}
                  </Typography>
                  <List dense>
                    {(() => {
                      const orderedTypes = currentPage === 'first' 
                        ? ['letterhead', 'patientinfo', 'subject', 'body'] 
                        : ['body'];
                      
                      const existingAreas = layout.areas
                        .filter(area => area.page === currentPage)
                        .reduce((acc, area) => {
                          acc[area.type] = area;
                          return acc;
                        }, {});
                      
                      return orderedTypes.map(type => {
                        const existingArea = existingAreas[type];
                        
                        if (existingArea) {
                          return (
                            <ListItem
                              key={existingArea.id}
                              sx={{
                                border: 1,
                                borderColor: selectedAreaId === existingArea.id ? AREA_TYPES[type].color : 'divider',
                                borderRadius: 1,
                                mb: 1,
                                cursor: 'pointer',
                                p: 1
                              }}
                              onClick={() => setSelectedAreaId(existingArea.id)}
                            >
                              <Avatar
                                sx={{ 
                                  bgcolor: AREA_TYPES[type].color, 
                                  width: 20, 
                                  height: 20, 
                                  mr: 1
                                }}
                              >
                                {React.createElement(AREA_TYPES[type].icon, { sx: { fontSize: 12 } })}
                              </Avatar>
                              <ListItemText
                                primary={t(`admin.reportingManagement.reportLayout.areas.${type}`)}
                                secondary={`${Math.round(existingArea.x / 2)}, ${Math.round(existingArea.y / 2)}`}
                                primaryTypographyProps={{ fontSize: '0.8rem' }}
                                secondaryTypographyProps={{ fontSize: '0.7rem' }}
                              />
                            </ListItem>
                          );
                        } else {
                          return (
                            <ListItem
                              key={`missing-${type}`}
                              sx={{
                                border: 1,
                                borderColor: 'divider',
                                borderStyle: 'dashed',
                                borderRadius: 1,
                                mb: 1,
                                cursor: 'pointer',
                                p: 1,
                                opacity: 0.7
                              }}
                              onClick={() => handleAddArea(type)}
                            >
                              <Avatar
                                sx={{ 
                                  bgcolor: AREA_TYPES[type].color, 
                                  width: 20, 
                                  height: 20, 
                                  mr: 1,
                                  opacity: 0.7
                                }}
                              >
                                <AddIcon sx={{ fontSize: 12 }} />
                              </Avatar>
                              <ListItemText
                                primary={t(`admin.reportingManagement.reportLayout.areas.${type}`)}
                                secondary={t('admin.reportingManagement.reportLayout.areas.clickToAdd')}
                                primaryTypographyProps={{ fontSize: '0.8rem', fontStyle: 'italic' }}
                                secondaryTypographyProps={{ fontSize: '0.7rem', fontStyle: 'italic' }}
                              />
                            </ListItem>
                          );
                        }
                      });
                    })()}
                  </List>
                </CardContent>
              </Card>
            </Box>

            {/* Area Configuration (rechte Spalte der unteren Zeile) */}
            <Box sx={{ flex: 1 }}>
              {selectedArea && (
                <Card sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar
                          sx={{ 
                            bgcolor: AREA_TYPES[selectedArea.type].color, 
                            width: 24, 
                            height: 24, 
                            mr: 1
                          }}
                        >
                          {React.createElement(AREA_TYPES[selectedArea.type].icon, { sx: { fontSize: 12 } })}
                        </Avatar>
                        <Typography variant="subtitle2">
                          {t(`admin.reportingManagement.reportLayout.areas.${selectedArea.type}`)}
                        </Typography>
                      </Box>
                      <Tooltip title={t('admin.reportingManagement.reportLayout.areas.delete')}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleAreaDelete(selectedArea.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>
                      {t('admin.reportingManagement.reportLayout.areas.position')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                      <TextField
                        size="small"
                        label={t('admin.reportingManagement.reportLayout.areas.x')}
                        type="number"
                        value={Math.round(selectedArea.x / 2)}
                        onChange={(e) => handleAreaUpdate(selectedArea.id, { x: Number(e.target.value) * 2 })}
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        size="small"
                        label={t('admin.reportingManagement.reportLayout.areas.y')}
                        type="number"
                        value={Math.round(selectedArea.y / 2)}
                        onChange={(e) => handleAreaUpdate(selectedArea.id, { y: Number(e.target.value) * 2 })}
                        sx={{ flex: 1 }}
                      />
                    </Box>

                    <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>
                      {t('admin.reportingManagement.reportLayout.areas.size')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                      <TextField
                        size="small"
                        label={t('admin.reportingManagement.reportLayout.areas.width')}
                        type="number"
                        value={Math.round(selectedArea.width / 2)}
                        onChange={(e) => handleAreaUpdate(selectedArea.id, { width: Number(e.target.value) * 2 })}
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        size="small"
                        type="number"
                        value={canvasUnitsToLines(selectedArea.height, selectedArea.fontSize)}
                        onChange={(e) => {
                          const newHeight = linesToCanvasUnits(Number(e.target.value), selectedArea.fontSize);
                          handleAreaUpdate(selectedArea.id, { height: newHeight });
                        }}
                        sx={{ flex: 1 }}
                        label={t('admin.reportingManagement.reportLayout.layoutAreas.heightInLines')}
                      />
                    </Box>

                    <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>
                      {t('admin.reportingManagement.reportLayout.areas.fontSize')}
                    </Typography>
                    <TextField
                      size="small"
                      type="number"
                      value={selectedArea.fontSize}
                      onChange={(e) => {
                        const newFontSize = Number(e.target.value);
                        const currentLines = canvasUnitsToLines(selectedArea.height, selectedArea.fontSize);
                        const newHeight = linesToCanvasUnits(currentLines, newFontSize);
                        handleAreaUpdate(selectedArea.id, { 
                          fontSize: newFontSize,
                          height: newHeight
                        });
                      }}
                      sx={{ width: '100%', mb: 1 }}
                    />

                    <Typography variant="caption" sx={{ mb: 0.5, display: 'block' }}>
                      {t('admin.reportingManagement.reportLayout.areas.fontFamily')}
                    </Typography>
                      <FormControl size="small" sx={{ width: '100%', mb: 1 }}>
                        <InputLabel id="font-family-label"></InputLabel>
                        <Select
                          labelId="font-family-label"
                          value={selectedArea.fontFamily || '"Segoe UI"'}
                          label=""
                          onChange={(e) => handleAreaUpdate(selectedArea.id, { fontFamily: e.target.value })}
                      >
                        {AVAILABLE_FONTS.map((font) => (
                          <MenuItem key={font.value} value={font.value} style={{ fontFamily: font.value }}>
                            {font.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </CardContent>
                </Card>
              )}
            </Box>
          </Box>

          {/* 3. Reihe: Betreff-Konfiguration */}
          <Card sx={{ mt: 1, height: 105 }}>
            <CardContent sx={{ p: 1.5, height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="subtitle2">
                  {t('admin.reportingManagement.reportLayout.subject.title')}
                </Typography>
                <Button
                  size="small"
                  onClick={() => setSubjectDialogOpen(true)}
                  className="button"
                >
                  <TextIcon />
                </Button>
              </Box>
              <TextField
                multiline
                rows={1}
                fullWidth
                size="small"
                value={layout.subjectTemplate}
                onChange={(e) => setLayout(prev => ({ ...prev, subjectTemplate: e.target.value }))}
                placeholder={t('admin.reportingManagement.reportLayout.subject.placeholder')}
              />
            </CardContent>
          </Card>

          {/* 4. Reihe: Begrüßung/Einleitung-Konfiguration */}
          <Card sx={{ mt: 1 }}>
            <CardContent sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">
                  {t('admin.reportingManagement.reportLayout.greeting.title')}
                </Typography>
                <Button
                  size="small"
                  onClick={() => setGreetingDialogOpen(true)}
                  className="button"
                >
                  <TextIcon />
                </Button>
              </Box>
              
              <Box sx={{ borderBottom: 0, borderColor: 'divider' }}>
                <Tabs 
                  value={greetingTabValue} 
                  onChange={(event, newValue) => setGreetingTabValue(newValue)}
                  variant="fullWidth"
                >
                  <Tab label={t('admin.reportingManagement.reportLayout.greeting.tabs.male')} />
                  <Tab label={t('admin.reportingManagement.reportLayout.greeting.tabs.female')} />
                  <Tab label={t('admin.reportingManagement.reportLayout.greeting.tabs.diverse')} />
                  <Tab label={t('admin.reportingManagement.reportLayout.greeting.tabs.none')} />
                </Tabs>
              </Box>
              
              <Box sx={{ pt: 2 }}>
                {greetingTabValue === 0 && (
                  <TextField
                    multiline
                    rows={4}
                    fullWidth
                    size="small"
                    value={layout.greetingTemplates.male}
                    onChange={(e) => setLayout(prev => ({ 
                      ...prev, 
                      greetingTemplates: { ...prev.greetingTemplates, male: e.target.value }
                    }))}
                    placeholder={t('admin.reportingManagement.reportLayout.greeting.placeholders.male')}
                  />
                )}
                {greetingTabValue === 1 && (
                  <TextField
                    multiline
                    rows={4}
                    fullWidth
                    size="small"
                    value={layout.greetingTemplates.female}
                    onChange={(e) => setLayout(prev => ({ 
                      ...prev, 
                      greetingTemplates: { ...prev.greetingTemplates, female: e.target.value }
                    }))}
                    placeholder={t('admin.reportingManagement.reportLayout.greeting.placeholders.female')}
                  />
                )}
                {greetingTabValue === 2 && (
                  <TextField
                    multiline
                    rows={4}
                    fullWidth
                    size="small"
                    value={layout.greetingTemplates.diverse}
                    onChange={(e) => setLayout(prev => ({ 
                      ...prev, 
                      greetingTemplates: { ...prev.greetingTemplates, diverse: e.target.value }
                    }))}
                    placeholder={t('admin.reportingManagement.reportLayout.greeting.placeholders.diverse')}
                  />
                )}
                {greetingTabValue === 3 && (
                  <TextField
                    multiline
                    rows={4}
                    fullWidth
                    size="small"
                    value={layout.greetingTemplates.none}
                    onChange={(e) => setLayout(prev => ({ 
                      ...prev, 
                      greetingTemplates: { ...prev.greetingTemplates, none: e.target.value }
                    }))}
                    placeholder={t('admin.reportingManagement.reportLayout.greeting.placeholders.none')}
                  />
                )}
              </Box>
            </CardContent>
          </Card>

          {/* 5. Reihe: Grußformel-Konfiguration */}
          <Card sx={{ mt: 1, height: 105 }}>
            <CardContent sx={{ p: 1.5, height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="subtitle2">
                  {t('admin.reportingManagement.reportLayout.closingGreeting.title')}
                </Typography>
              </Box>
              <TextField
                multiline
                rows={1}
                fullWidth
                size="small"
                value={layout.greetingEnd}
                onChange={(e) => setLayout(prev => ({ ...prev, greetingEnd: e.target.value }))}
                placeholder={t('admin.reportingManagement.reportLayout.closingGreeting.placeholder')}
              />
            </CardContent>
          </Card>
        </Box>

        {/* Rechte Spalte (60%) - Vorschau */}
        <Box sx={{ 
          width: { xs: '100%', lg: 'auto' }, 
          minWidth: 400,
          flex: { xs: 'none', lg: '1' }
        }}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 936, boxSizing: 'border-box' }}>
            {/* Toolbar */}
            <Toolbar variant="dense" sx={{ minHeight: 48, mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                {/* Seiten-Tabs */}
                <Button
                  variant={currentPage === 'first' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => handlePageChange('first')}
                  className={currentPage === 'first' ? 'button' : ''}
                >
                  {t('admin.reportingManagement.reportLayout.preview.firstPage')}
                </Button>
                <Button
                  variant={currentPage === 'following' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => handlePageChange('following')}
                  className={currentPage === 'following' ? 'button' : ''}
                >
                  {t('admin.reportingManagement.reportLayout.preview.followingPage')}
                </Button>

                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                {/* Zoom Controls */}
                <IconButton size="small" onClick={() => setZoom(prev => Math.max(0.5, prev - 0.1311))}>
                  <ZoomOutIcon />
                </IconButton>
                <Button
                  size="small"
                  sx={{ 
                    color: 'text.primary',
                    fontSize: '0.75rem',
                    minWidth: 50,
                    px: 1,
                    mx: 0.5
                  }}
                >
                  {Math.round((zoom / 1.311) * 100)}%
                </Button>
                <IconButton size="small" onClick={() => setZoom(prev => Math.min(4, prev + 0.1311))}>
                  <ZoomInIcon />
                </IconButton>
                <Box sx={{ mx: 1, display: 'flex', gap: 0.5 }}>
                  <Button
                    size="small"
                    onClick={() => setZoom(1.311)}
                    sx={{ 
                      color: 'orange', 
                      fontSize: '0.75rem',
                      minWidth: 'auto',
                      px: 1
                    }}
                  >
                    100%
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setZoom(1.9665)}
                    sx={{ 
                      color: 'orange', 
                      fontSize: '0.75rem',
                      minWidth: 'auto',
                      px: 1
                    }}
                  >
                    150%
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setZoom(2.622)}
                    sx={{ 
                      color: 'orange', 
                      fontSize: '0.75rem',
                      minWidth: 'auto',
                      px: 1
                    }}
                  >
                    200%
                  </Button>
                </Box>
                
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                
                {/* Additional Controls */}
                <Tooltip title={t('admin.reportingManagement.reportLayout.preview.showGrid')}>
                  <IconButton 
                    size="small" 
                    color={showGrid ? 'primary' : 'default'}
                    onClick={() => setShowGrid(!showGrid)}
                  >
                    <GridIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('admin.reportingManagement.reportLayout.preview.showRulers')}>
                  <IconButton 
                    size="small" 
                    color={showRulers ? 'primary' : 'default'}
                    onClick={() => setShowRulers(!showRulers)}
                  >
                    <RulerIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('admin.reportingManagement.reportLayout.preview.showMargins')}>
                  <IconButton 
                    size="small" 
                    color={showMargins ? 'primary' : 'default'}
                    onClick={() => setShowMargins(!showMargins)}
                  >
                    <MarginsIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Toolbar>

            {/* Canvas Container mit fester Höhe für Scrollbars */}
            <Box sx={{ 
              flex: 1,
              overflow: 'auto',
              position: 'relative',
              backgroundColor: 'var(--rd-bg-default)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              p: 3
            }}>
              <Box
                sx={{
                  position: 'relative',
                  width: canvasWidth,
                  height: canvasHeight
                }}
              >
                {renderRulers()}
                <Box sx={{ position: 'absolute', top: 25, left: 25 }}>
                  {renderPage()}
                </Box>
              </Box>
            </Box>
      </Paper>
        </Box>
      </Box>

      {/* Subject Dialog */}
      <Dialog 
        open={subjectDialogOpen} 
        onClose={() => setSubjectDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('admin.reportingManagement.reportLayout.subject.dialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('admin.reportingManagement.reportLayout.subject.dialog.description')}
          </DialogContentText>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {DATABASE_FIELDS.map(field => (
              <Button
                key={field}
                variant="outlined"
                size="small"
                onClick={() => insertDatabaseField(field)}
                className="button"
              >
                {t(`admin.reportingManagement.reportLayout.fields.${field}`)}
              </Button>
            ))}
          </Box>
          <TextField
            multiline
            rows={6}
            fullWidth
            value={layout.subjectTemplate}
            onChange={(e) => setLayout(prev => ({ ...prev, subjectTemplate: e.target.value }))}
            placeholder={t('admin.reportingManagement.reportLayout.subject.placeholder')}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setSubjectDialogOpen(false)}
            className="button"
          >
            {t('admin.reportingManagement.reportLayout.subject.dialog.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Greeting Dialog */}
      <Dialog 
        open={greetingDialogOpen} 
        onClose={() => setGreetingDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>{t('admin.reportingManagement.reportLayout.greeting.dialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('admin.reportingManagement.reportLayout.greeting.dialog.description')}
          </DialogContentText>
          
          <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" sx={{ width: '100%', mb: 1 }}>{t('admin.reportingManagement.reportLayout.greeting.dialog.availablePlaceholders')}</Typography>
            {DATABASE_FIELDS.map(field => (
              <Button
                key={field}
                variant="outlined"
                size="small"
                onClick={() => {
                  document.execCommand('insertText', false, `[${field}]`);
                }}
                className="button"
              >
                {t(`admin.reportingManagement.reportLayout.fields.${field}`)}
              </Button>
            ))}
          </Box>

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs 
              value={greetingTabValue} 
              onChange={(event, newValue) => setGreetingTabValue(newValue)}
              variant="fullWidth"
            >
              <Tab label={t('admin.reportingManagement.reportLayout.greeting.tabs.male')} />
              <Tab label={t('admin.reportingManagement.reportLayout.greeting.tabs.female')} />
              <Tab label={t('admin.reportingManagement.reportLayout.greeting.tabs.diverse')} />
              <Tab label={t('admin.reportingManagement.reportLayout.greeting.tabs.none')} />
            </Tabs>
          </Box>

          <Box>
            {greetingTabValue === 0 && (
              <TextField
                multiline
                rows={6}
                fullWidth
                value={layout.greetingTemplates.male}
                onChange={(e) => setLayout(prev => ({ 
                  ...prev, 
                  greetingTemplates: { ...prev.greetingTemplates, male: e.target.value }
                }))}
                placeholder={t('admin.reportingManagement.reportLayout.greeting.placeholders.male')}
              />
            )}
            {greetingTabValue === 1 && (
              <TextField
                multiline
                rows={6}
                fullWidth
                value={layout.greetingTemplates.female}
                onChange={(e) => setLayout(prev => ({ 
                  ...prev, 
                  greetingTemplates: { ...prev.greetingTemplates, female: e.target.value }
                }))}
                placeholder={t('admin.reportingManagement.reportLayout.greeting.placeholders.female')}
              />
            )}
            {greetingTabValue === 2 && (
              <TextField
                multiline
                rows={6}
                fullWidth
                value={layout.greetingTemplates.diverse}
                onChange={(e) => setLayout(prev => ({ 
                  ...prev, 
                  greetingTemplates: { ...prev.greetingTemplates, diverse: e.target.value }
                }))}
                placeholder={t('admin.reportingManagement.reportLayout.greeting.placeholders.diverse')}
              />
            )}
            {greetingTabValue === 3 && (
              <TextField
                multiline
                rows={6}
                fullWidth
                value={layout.greetingTemplates.none}
                onChange={(e) => setLayout(prev => ({ 
                  ...prev, 
                  greetingTemplates: { ...prev.greetingTemplates, none: e.target.value }
                }))}
                placeholder={t('admin.reportingManagement.reportLayout.greeting.placeholders.none')}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setGreetingDialogOpen(false)}
            className="button"
          >
            {t('admin.reportingManagement.reportLayout.greeting.dialog.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReportLayout; 
